import * as w from '@packages/systems/wfdl';
import * as ast from '@packages/systems/wfdl-ast';
import * as wq from '@packages/systems/wfdl-Qualified';

type Cases = {
  [name: string]: {
    type: w.Type;
    text: string;
    pretty?: boolean;
  };
};

const tyName1 = wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('Thing1'));
const tyName2 = wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('Thing2'));
const tyName3 = wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('Thing3'));

/**
 * Cases used for testing the WFDL Type parser.
 */
export const cases: Cases = {
  'Boolean primitive': {
    pretty: true,
    text: 'Boolean',
    type: ast.TBoolean,
  },
  'Text primitive': {
    pretty: true,
    text: 'Text',
    type: ast.TText,
  },
  'Number primitive': {
    pretty: true,
    text: 'Number',
    type: ast.TNumber,
  },
  'Element primitive': {
    pretty: true,
    text: 'Element',
    type: ast.TElement,
  },
  'primitive list (longhand)': {
    pretty: true,
    text: 'List<Boolean>',
    type: ast.TList(ast.TBoolean),
  },
  'list of record': {
    pretty: true,
    text: `
      List<{
        a: Boolean,
        b: Number,
        c: Text,
      }>
    `,
    type: ast.TList(
      ast.TRecord({
        a: ast.TBoolean,
        b: ast.TNumber,
        c: ast.TText,
      })
    ),
  },
  'bare type reference': {
    pretty: true,
    text: 'T',
    type: ast.TTypeReference('T'),
  },
  'bare type constructor': {
    pretty: true,
    text: 'MyPlugin::Thing1',
    type: ast.TTypeConstructor(
      wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('Thing1'))
    ),
  },
  'empty row': {
    pretty: true,
    text: 'RowEmpty',
    type: ast.TRowEmpty,
  },
  'bare row constrution': {
    pretty: true,
    type: ast.constructRow({
      items: {
        a: ast.TTypeConstructor(tyName1),
        b: ast.TTypeConstructor(tyName2),
        c: ast.TTypeConstructor(tyName3),
      },
      tail: ast.TRowEmpty,
    }),
    text: [
      '{%',
      '  a: MyPlugin::Thing1,',
      '  b: MyPlugin::Thing2,',
      '  c: MyPlugin::Thing3,',
      '%}',
    ].join('\n'),
  },
  'empty record': {
    pretty: true,
    text: '{}',
    type: ast.TRecord({}),
  },
  'record type constructor': {
    text: 'Record',
    type: ast.TTypeConstructor(
      wq.qualify(w.Plugins.name('Builtin'), wq.typeName('Record'))
    ),
  },
  'single-item record': {
    pretty: true,
    text: `
      {
        a: Boolean,
      }
    `,
    type: ast.TRecord({a: ast.TBoolean}),
  },
  'multi-item record': {
    pretty: true,
    text: `
      {
        a: Boolean,
        b: Number,
        c: Text,
      }
    `,
    type: ast.TRecord({
      a: ast.TBoolean,
      b: ast.TNumber,
      c: ast.TText,
    }),
  },
  'record with trailing commas and tail (with trailing comma)': {
    pretty: true,
    text: `
      {
        a: Boolean,
        b: Number,
        c: Text,
        ...Tail,
      }
    `,
    type: ast.TRecord(
      {
        a: ast.TBoolean,
        b: ast.TNumber,
        c: ast.TText,
      },
      ast.TTypeReference('Tail')
    ),
  },
  'nested records': {
    pretty: true,
    text: `
      {
        a: Boolean,
        b: Number,
        c: Text,
        d: {
          e: Boolean,
          f: Number,
          g: Text,
          h: {
            i: Boolean,
            j: Number,
            k: Text,
          },
        },
      }
    `,
    type: ast.TRecord({
      a: ast.TBoolean,
      b: ast.TNumber,
      c: ast.TText,
      d: ast.TRecord({
        e: ast.TBoolean,
        f: ast.TNumber,
        g: ast.TText,
        h: ast.TRecord({
          i: ast.TBoolean,
          j: ast.TNumber,
          k: ast.TText,
        }),
      }),
    }),
  },
  'simple function': {
    pretty: true,
    text: 'Number => Boolean',
    type: ast.functionOf(ast.TNumber, ast.TBoolean),
  },
  'curried function': {
    pretty: true,
    text: 'Number => Text => Boolean',
    type: ast.functionOf(ast.TNumber, ast.functionOf(ast.TText, ast.TBoolean)),
  },
  'function with function argument': {
    pretty: true,
    text: '(Number => Text) => Boolean',
    type: ast.functionOf(ast.functionOf(ast.TNumber, ast.TText), ast.TBoolean),
  },
  'curried function with function arguments': {
    pretty: true,
    text: '(Number => Text) => (Number => Text) => Boolean',
    type: ast.functionOf(
      ast.functionOf(ast.TNumber, ast.TText),
      ast.functionOf(ast.functionOf(ast.TNumber, ast.TText), ast.TBoolean)
    ),
  },
  'simple generic': {
    pretty: true,
    text: '<T>(T)',
    type: ast.TGeneric({
      arg: 'T',
      body: ast.TTypeReference('T'),
    }),
  },
  'multi-arg generic': {
    pretty: true,
    text: '<T, R>(T => R)',
    type: ast.TGeneric({
      arg: 'T',
      body: ast.TGeneric({
        arg: 'R',
        body: ast.functionOf(ast.TTypeReference('T'), ast.TTypeReference('R')),
      }),
    }),
  },
  'multi-arg generic with record body': {
    pretty: true,
    text: [
      '<T, R>(MyPlugin::Thing1<{%',
      '  first: MyPlugin::Thing2,',
      '  second: MyPlugin::Thing3,',
      '  ...R,',
      '%}>)',
    ].join('\n'),
    type: ast.TGeneric({
      arg: 'T',
      body: ast.TGeneric({
        arg: 'R',
        body: ast.constructTypeApplication({
          con: tyName1,
          args: [
            ast.constructRow({
              items: {
                first: ast.TTypeConstructor(tyName2),
                second: ast.TTypeConstructor(tyName3),
              },
              tail: ast.TTypeReference('R'),
            }),
          ],
        }),
      }),
    }),
  },
  'simple type application (with builtin param)': {
    pretty: true,
    text: 'MyPlugin::MyType<Boolean>',
    type: ast.constructTypeApplication({
      con: wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('MyType')),
      args: [ast.TBoolean],
    }),
  },
  'simple type application (with plugin param)': {
    pretty: true,
    text: 'MyPlugin::MyType<MyPlugin::MyOtherType>',
    type: ast.constructTypeApplication({
      con: wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('MyType')),
      args: [
        ast.TTypeConstructor(
          wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('MyOtherType'))
        ),
      ],
    }),
  },
  'multi-arg type application': {
    pretty: true,
    text: 'MyPlugin::MyType<Boolean, Text>',
    type: ast.constructTypeApplication({
      con: wq.qualify(w.Plugins.name('MyPlugin'), wq.typeName('MyType')),
      args: [ast.TBoolean, ast.TText],
    }),
  },
  'simple union': {
    pretty: true,
    text: `
      Union<{
        a: Boolean,
        b: Number,
        c: Text,
      }>
    `,
    type: ast.TUnion({
      a: ast.TBoolean,
      b: ast.TNumber,
      c: ast.TText,
    }),
  },
  'union with tail': {
    pretty: true,
    text: `
      Union<{
        a: Boolean,
        b: Number,
        c: Text,
        ...Tail,
      }>
    `,
    type: ast.TUnion(
      {
        a: ast.TBoolean,
        b: ast.TNumber,
        c: ast.TText,
      },
      ast.TTypeReference('Tail')
    ),
  },
  'simple enum': {
    pretty: true,
    text: `
      Enum<{
        small: "Small",
        medium: "Medium",
        large: "Large",
      }>
    `,
    type: ast.TEnum({
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
    }),
  },
  'enum with quoted keys': {
    pretty: true,
    text: `
      Enum<{
        "my-key": "My Key",
        normal: "Normal",
      }>
    `,
    type: ast.TEnum({'my-key': 'My Key', normal: 'Normal'}),
  },
  'labeled type': {
    pretty: true,
    text: `
      @label("My type")
      Boolean
    `,
    type: w.withSystem({label: 'My type'}, ast.TBoolean),
  },
  'true boolean attribute': {
    pretty: true,
    text: `
      @isBindable
      Boolean
    `,
    type: w.withSystem({isBindable: true}, ast.TBoolean),
  },
  'false boolean attribute': {
    pretty: true,
    text: `
      @isBindable(false)
      Boolean
    `,
    type: w.withSystem({isBindable: false}, ast.TBoolean),
  },
  'number attribute': {
    pretty: true,
    text: `
      @rank(5)
      Text
    `,
    type: w.withMeta({rank: 5}, ast.TText),
  },
  'type with multiple system and meta attributes': {
    pretty: true,
    text: `
      @isBindable
      @label("Hello")
      @variant("one")
      @rank(3)
      Boolean
    `,
    type: w.withMeta(
      {variant: 'one', rank: 3},
      w.withSystem({isBindable: true, label: 'Hello'}, ast.TBoolean)
    ),
  },
  'record with bindable field': {
    pretty: true,
    text: `
      {
        @isBindable
        a: Boolean,
        b: Number,
      }
    `,
    type: ast.TRecord({
      a: w.withSystem({isBindable: true}, ast.TBoolean),
      b: ast.TNumber,
    }),
  },
  'record with labeled fields': {
    pretty: true,
    text: `
      {
        @label("AAA")
        a: Boolean,
        @label("BBB")
        b: Number,
      }
    `,
    type: ast.TRecord({
      a: w.withSystem({label: 'AAA'}, ast.TBoolean),
      b: w.withSystem({label: 'BBB'}, ast.TNumber),
    }),
  },
  'labeled record': {
    pretty: true,
    text: `
      @label("Hello")
      @isBindable
      {
        a: Boolean,
      }
    `,
    type: w.withSystem(
      {label: 'Hello', isBindable: true},
      ast.TRecord({
        a: ast.TBoolean,
      })
    ),
  },
  'record field with system and meta fields': {
    pretty: true,
    text: `
      {
        alt: Text,
        @isBindable
        @label("Image")
        @variant("small")
        img: Asset::Image,
      }
    `,
    type: ast.TRecord({
      alt: ast.TText,
      img: w.withMeta(
        {variant: 'small'},
        w.withSystem(
          {
            isBindable: true,
            label: 'Image',
          },
          ast.TTypeConstructor(
            wq.qualify(w.Plugins.name('Asset'), wq.typeName('Image'))
          )
        )
      ),
    }),
  },
  'nested records that have fields with attributes': {
    pretty: true,
    text: `
      {
        a: Boolean,
        @isBindable
        b: Number,
        c: Text,
        d: {
          e: Boolean,
          f: Number,
          @label("Hello")
          g: Text,
          h: {
            @isBindable(false)
            @label("World")
            i: Boolean,
            j: Number,
            k: Text,
          },
        },
      }
    `,
    type: ast.TRecord({
      a: ast.TBoolean,
      b: w.withSystem(
        {
          isBindable: true,
        },
        ast.TNumber
      ),
      c: ast.TText,
      d: ast.TRecord({
        e: ast.TBoolean,
        f: ast.TNumber,
        g: w.withSystem(
          {
            label: 'Hello',
          },
          ast.TText
        ),
        h: ast.TRecord({
          i: w.withSystem(
            {
              isBindable: false,
              label: 'World',
            },
            ast.TBoolean
          ),
          j: ast.TNumber,
          k: ast.TText,
        }),
      }),
    }),
  },
  'list of elements': {
    pretty: true,
    text: 'List<Element>',
    type: ast.TList(ast.TElement),
  },
  'rich text children type': {
    pretty: true,
    text: 'Basic::RichTextChildren',
    type: ast.TTypeConstructor(
      wq.qualify(w.Plugins.name('Basic'), wq.typeName('RichTextChildren'))
    ),
  },
  'default value with abstract node POJOs': {
    pretty: true,
    text: `
      @defaultValue([{"id":"new-element-temp","text":true,"data":{"value":"temp"},"children":[]}])
      Element
    `,
    type: w.withSystem(
      {
        defaultValue: [
          {
            id: 'new-element-temp',
            text: true,
            data: {value: 'temp'},
            children: [],
          },
        ],
      },
      ast.TElement
    ),
  },
  'primitive list (shorthand)': {
    text: 'Boolean[]',
    type: ast.TList(ast.TBoolean),
  },
  'naked type reference': {
    text: 'T',
    type: ast.TTypeReference('T'),
  },
  'record with trailing commas and tail': {
    text: `
      {
        a: Boolean,
        b: Number,
        c: Text,
        ...Tail
      }
    `,
    type: ast.TRecord(
      {
        a: ast.TBoolean,
        b: ast.TNumber,
        c: ast.TText,
      },
      ast.TTypeReference('Tail')
    ),
  },
  'simple function with parens': {
    text: '(Number) => Boolean',
    type: ast.functionOf(ast.TNumber, ast.TBoolean),
  },
  'function with multiple params (syntax sugar)': {
    text: '(Number, Text) => Boolean',
    type: ast.functionOf(ast.TNumber, ast.functionOf(ast.TText, ast.TBoolean)),
  },
  'function with multiple function arguments (syntax sugar)': {
    text: '((Number => Text), (Number => Text)) => Boolean',
    type: ast.functionOf(
      ast.functionOf(ast.TNumber, ast.TText),
      ast.functionOf(ast.functionOf(ast.TNumber, ast.TText), ast.TBoolean)
    ),
  },
  'silliness with parentheses (1)': {
    text: '((Boolean, Text) => Number)',
    type: ast.functionOf(ast.TBoolean, ast.functionOf(ast.TText, ast.TNumber)),
  },
  'silliness with parentheses (2)': {
    text: '(Boolean, Text) => (Number)',
    type: ast.functionOf(ast.TBoolean, ast.functionOf(ast.TText, ast.TNumber)),
  },
  'silliness with parentheses (3)': {
    text: '(   (  ( (Boolean, Text) => ((Number)) )  )   )',
    type: ast.functionOf(ast.TBoolean, ast.functionOf(ast.TText, ast.TNumber)),
  },
  'simple generic with extra parentheses': {
    text: '<T>(((T)))',
    type: ast.TGeneric({
      arg: 'T',
      body: ast.TTypeReference('T'),
    }),
  },
  'record with quoted GUID field name': {
    pretty: true,
    text: `
      {
        "75ce4434-00e7-a86f-be03-76fe8238d6e2": Boolean,
      }
    `,
    type: ast.TRecord({
      '75ce4434-00e7-a86f-be03-76fe8238d6e2': ast.TBoolean,
    }),
  },
  'record with quoted field name with attributes': {
    pretty: true,
    text: `
      {
        @label("Content")
        @isBindable
        "75ce4434-00e7-a86f-be03-76fe8238d6e2": Boolean,
      }
    `,
    type: ast.TRecord({
      '75ce4434-00e7-a86f-be03-76fe8238d6e2': w.withSystem(
        {label: 'Content', isBindable: true},
        ast.TBoolean
      ),
    }),
  },
  comments: {
    text: `
      // I'm a comment
      /// I'm a doc-comment
      Boolean
      // I'm also a comment!
    `,
    type: ast.TBoolean,
  },
};
